/**
 * Page admin Émargements V3 (Rafik 02/06/2026, Phase 3).
 *
 * Liste filtrable des séances d'émargement (toutes formateurs confondus).
 * KPIs globaux en haut. Bouton "Nouvelle séance" qui ouvre une modal.
 *
 * Sous-page espace formateur : `/emargements/formateur/:id` (composant
 * dédié plus loin, charge le même endpoint avec filtre formateurZohoId).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PenTool,
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle,
  Copy,
  Search,
  Users,
  Loader2,
} from 'lucide-react';
import { NouvelleSeanceModal } from '../components/NouvelleSeanceModal.js';

export interface SeanceDTO {
  id: string;
  formCode: string;
  formVersion: number;
  status: 'pending' | 'awaiting_signature' | 'submitted' | 'expired';
  token: string;
  answers: Record<string, unknown> | null;
  submittedAt: string | null;
  formateurZohoId: string | null;
  destinataireNom: string;
  destinataireEmail: string;
  secondaryToken: string | null;
  secondaryAnswers: Record<string, unknown> | null;
  secondarySubmittedAt: string | null;
  secondaryDestinataireNom: string | null;
  secondaryDestinataireEmail: string | null;
  apprenantZohoId: string;
  issuedAt: string;
  expiresAt: string;
}

const API_BASE =
  typeof window !== 'undefined' && window.location.host.startsWith('cockpit-v2.')
    ? 'https://cockpit.andalous-elearning.com/api/v1'
    : '/api/v1';

async function fetchSeances(filters: {
  status?: string;
  formateurZohoId?: string;
}): Promise<SeanceDTO[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.formateurZohoId) params.set('formateurZohoId', filters.formateurZohoId);
  const r = await fetch(`${API_BASE}/v2/emargement/seances?${params}`, {
    credentials: 'include',
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const json = (await r.json()) as { data: { seances: SeanceDTO[] } | null; error: unknown };
  return json.data?.seances ?? [];
}

const STATUS_LABEL: Record<SeanceDTO['status'], string> = {
  pending: 'En attente prof',
  awaiting_signature: 'En attente signature',
  submitted: 'Signée',
  expired: 'Expirée',
};

const STATUS_COLOR: Record<SeanceDTO['status'], string> = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  awaiting_signature: 'bg-sky-100 text-sky-700 border-sky-200',
  submitted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  expired: 'bg-rose-100 text-rose-700 border-rose-200',
};

export function EmargementsPage() {
  const [seances, setSeances] = useState<SeanceDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const reload = () => {
    setIsLoading(true);
    setError(null);
    fetchSeances({ status: filterStatus || undefined })
      .then((data) => {
        setSeances(data);
        setIsLoading(false);
      })
      .catch((e) => {
        setError((e as Error).message);
        setIsLoading(false);
      });
  };

  useEffect(reload, [filterStatus]);

  const kpis = useMemo(() => {
    const total = seances.length;
    const signees = seances.filter((s) => s.status === 'submitted').length;
    const enAttente = seances.filter(
      (s) => s.status === 'pending' || s.status === 'awaiting_signature',
    ).length;
    const satisfactions = seances
      .map((s) => (s.secondaryAnswers as Record<string, unknown> | null)?.['satisfaction'])
      .map((n) => (typeof n === 'number' ? n : Number(n)))
      .filter((n) => !isNaN(n) && n > 0);
    const satMoy =
      satisfactions.length > 0
        ? satisfactions.reduce((a, b) => a + b, 0) / satisfactions.length
        : null;
    return { total, signees, enAttente, satMoy };
  }, [seances]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return seances;
    const q = searchQuery.toLowerCase();
    return seances.filter(
      (s) =>
        s.destinataireNom.toLowerCase().includes(q) ||
        (s.secondaryDestinataireNom ?? '').toLowerCase().includes(q),
    );
  }, [seances, searchQuery]);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-900">
            <PenTool className="h-6 w-6 text-[#c89b3c]" />
            Émargements
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Pilotage des feuilles d'émargement de séance — flow prof remplit → élève signe.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 rounded-lg bg-[#0a2540] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0a2540]/90"
        >
          <Plus className="h-4 w-4" />
          Nouvelle séance
        </button>
      </header>

      {/* KPIs */}
      <div className="mb-6 grid grid-cols-4 gap-4">
        <KpiCard
          label="Total séances"
          value={kpis.total}
          icon={<PenTool className="h-4 w-4" />}
          color="neutral"
        />
        <KpiCard
          label="Signées"
          value={kpis.signees}
          icon={<CheckCircle2 className="h-4 w-4" />}
          color="emerald"
        />
        <KpiCard
          label="En attente"
          value={kpis.enAttente}
          icon={<Clock className="h-4 w-4" />}
          color="amber"
        />
        <KpiCard
          label="Satisfaction moyenne"
          value={kpis.satMoy !== null ? `${kpis.satMoy.toFixed(1)} / 5` : '—'}
          icon={<Users className="h-4 w-4" />}
          color="sky"
        />
      </div>

      {/* Filtres */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par formateur ou élève…"
            className="w-full rounded-lg border border-neutral-200 bg-white py-2 pl-10 pr-3 text-sm placeholder:text-neutral-400 focus:border-[#0a2540] focus:outline-none"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-[#0a2540] focus:outline-none"
        >
          <option value="">Tous statuts</option>
          <option value="pending">En attente prof</option>
          <option value="awaiting_signature">En attente signature</option>
          <option value="submitted">Signée</option>
          <option value="expired">Expirée</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement des séances…
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-8 text-sm text-rose-700">
            <AlertCircle className="h-4 w-4" />
            Erreur : {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-neutral-500">
            Aucune séance d'émargement pour les filtres actuels.
            <br />
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#0a2540] hover:underline"
            >
              <Plus className="h-3 w-3" />
              Créer la première séance
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-neutral-600">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Formateur</th>
                <th className="px-4 py-3">Élève</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Satisf.</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((s) => (
                <SeanceRow key={s.id} seance={s} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <NouvelleSeanceModal
          onClose={() => setModalOpen(false)}
          onCreated={() => {
            setModalOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'neutral' | 'emerald' | 'amber' | 'sky';
}) {
  const colorMap = {
    neutral: 'border-neutral-200 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
    sky: 'border-sky-200 bg-sky-50',
  };
  return (
    <div className={`rounded-lg border p-4 ${colorMap[color]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-600">
          {label}
        </span>
        <span className="text-neutral-400">{icon}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-neutral-900">{value}</div>
    </div>
  );
}

function SeanceRow({ seance }: { seance: SeanceDTO }) {
  const dateSeance =
    typeof seance.answers?.date_seance === 'string'
      ? seance.answers.date_seance
      : seance.issuedAt.slice(0, 10);
  const satisfaction = seance.secondaryAnswers?.['satisfaction'];
  const linkProf = `${window.location.origin}/emargements/by-token/${seance.token}`;
  const linkEleve = seance.secondaryToken
    ? `${window.location.origin}/emargements/by-secondary/${seance.secondaryToken}`
    : null;

  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-4 py-3 font-mono text-xs text-neutral-700">{dateSeance}</td>
      <td className="px-4 py-3">
        <div className="font-semibold text-neutral-900">{seance.destinataireNom}</div>
        {seance.formateurZohoId && (
          <Link
            to={`/emargements/formateur/${seance.formateurZohoId}`}
            className="text-xs text-[#0a2540] hover:underline"
          >
            Voir espace formateur →
          </Link>
        )}
      </td>
      <td className="px-4 py-3 text-neutral-700">{seance.secondaryDestinataireNom ?? '—'}</td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[seance.status]}`}
        >
          {STATUS_LABEL[seance.status]}
        </span>
      </td>
      <td className="px-4 py-3 text-neutral-700">
        {typeof satisfaction === 'number' || (satisfaction && !isNaN(Number(satisfaction)))
          ? `${satisfaction} / 5`
          : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(linkProf);
            }}
            className="flex items-center gap-1 rounded border border-neutral-200 px-2 py-1 text-[11px] font-medium text-neutral-700 hover:bg-neutral-100"
            title="Copier le lien formateur (phase 1)"
          >
            <Copy className="h-3 w-3" /> Prof
          </button>
          {linkEleve && (
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(linkEleve);
              }}
              className="flex items-center gap-1 rounded border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-700 hover:bg-sky-100"
              title="Copier le lien élève (phase 2)"
            >
              <Copy className="h-3 w-3" /> Élève
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
